import React from 'react';
import { Button, CircularProgress } from '@mui/material';
import { styled } from '@mui/system';

const colors = {
  primary: {
    main: '#995a00',
    dark: '#804b00',
    light: '#bf7000',
    hover: 'rgba(153, 90, 0, 0.05)',
    focus: 'rgba(153, 90, 0, 0.12)',
    active: 'rgba(153, 90, 0, 0.30)',
    outlined: 'rgba(153, 90, 0, 0.50)',
    label: '#ffffff'
  },
  secondary: {
    main: '#ffa525',
    dark: '#ff9600',
    light: '#ffc066',
    hover: 'rgba(255, 165, 37, 0.08)',
    focus: 'rgba(255, 165, 37, 0.12)',
    active: 'rgba(255, 165, 37, 0.20)',
    outlined: 'rgba(255, 165, 37, 0.40)',
    label: '#ffffff'
  },
  error: {
    main: '#cc0000',
    dark: '#b20000',
    light: '#ff3333',
    hover: 'rgba(204, 0, 0, 0.08)',
    focus: 'rgba(204, 0, 0, 0.12)',
    active: 'rgba(204, 0, 0, 0.20)',
    outlined: '#cc0000',
    label: '#ffffff'
  },
  success: {
    main: '#008000',
    dark: '#006600',
    light: '#6cd96c',
    hover: 'rgba(0, 128, 0, 0.08)',
    focus: 'rgba(0, 128, 0, 0.12)',
    active: 'rgba(0, 128, 0, 0.20)',
    outlined: '#008000',
    label: '#ffffff'
  },
  neutral: {
    main: '#333333',
    dark: '#8c8c8c',
    label: '#333333',
    disabledBg: '#f2f2f2',
    disabledText: '#8c8c8c',
    outlined: '#8c8c8c'
  }
};

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) =>
    prop !== 'customcolor' && prop !== 'variantStyle'
})(({ customcolor = 'primary', variant = 'filled', disabled, isFocused  }) => {
  const colorSet = colors[customcolor] || colors.primary;

  const base = {
    borderRadius: 8,
    fontWeight: 600,
    fontSize: { xs: '13px', md: '16px' },
    lineHeight: { xs: '20px', md: '24px' },
    textTransform: 'none',
    padding: '6px 16px',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    ...(disabled && {
      backgroundColor: colorSet.disabledBg || '#e5e5e5',
      color: colorSet.disabledText || '#8c8c8c',
      border: variant === 'outlined' ? `2px solid #ccc` : 'none',
      cursor: 'not-allowed'
    }),
  };

  if (variant === 'filled' && !disabled) {
    base.backgroundColor = colorSet.main;
    base.color = colorSet.label;
    base['&:hover'] = { backgroundColor: colorSet.light };
    base['&:active'] = { backgroundColor: colorSet.active };
  }

  if (variant === 'outlined' && !disabled) {
    base.backgroundColor = 'transparent';
    base.color = colorSet.main;
    base.border = `2px solid ${colorSet.outlined}`;
    base['&:hover'] = {
      backgroundColor: colorSet.hover,
      border: `2px solid ${colorSet.outlined}`
    };
    base['&:active'] = {
      backgroundColor: colorSet.active,
      border: `2px solid ${colorSet.outlined}`
    };
  }

  if (variant === 'text' && !disabled) {
    base.backgroundColor = 'transparent';
    base.color = colorSet.main;
    base['&:hover'] = { backgroundColor: colorSet.hover };
    base['&:active'] = { backgroundColor: colorSet.active };
  }

  // if (isFocused && !disabled) {
  //   base.boxShadow = `0 0 0 3px ${colorSet.focus}`;
  // }
  return base;
});

const CustomButton = ({
  variant = 'filled',
  color = 'primary',
  label,
  disabled = false,
  loading = false,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  onClick,
  ...rest
}) => {
  const [isFocused, setIsFocused] = React.useState(false);

  // When loading is true, show spinner and disable the button
  const isDisabled = disabled || loading;
  
  // Determine which icon to show based on loading state
  const getStartIcon = () => {
    if (loading) {
      return <CircularProgress size={16} sx={{ color: 'currentColor' }} />;
    }
    return LeftIcon ? <LeftIcon /> : null;
  };

  return (
    <StyledButton
      variant={variant}
      customcolor={color}
      disabled={isDisabled}
      startIcon={getStartIcon()}
      endIcon={!loading && RightIcon ? <RightIcon /> : null}
      isFocused={isFocused}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onClick={onClick}
      {...rest}
    >
      {loading ? 'Loading...' : label}
    </StyledButton>
  );
};

export default CustomButton;
